import { useEffect, useRef } from 'react';
import { useAuth } from '@clerk/nextjs';
import { createAuthedClient } from '@/lib/supabase/client';
import { notifyAssetLibraryChanged } from '@/components/canvas/utils/asset-folders-events';

// 生成专用 Realtime：仅监听 Postgres Changes（表 generation 的行变更），不监听 broadcast
// 由上层传入 enabled 开关控制（通常来自 NEXT_PUBLIC_ENABLE_REALTIME），关闭时会主动退订
// 用于节点级生成完成/失败的最快反馈链路
// 入参类型
type UseGenerationRealtimeParams = {
  projectId: string;
  nodeId: string;
  generationId?: string;
  onCompleted: (url: string, metadata?: any) => void;
  onFailed?: (error?: string) => void;
  enabled?: boolean;
};

// 定义 Hook
export function useGenerationRealtime({ projectId, nodeId, generationId, onCompleted, onFailed, enabled = true }: UseGenerationRealtimeParams) {
  const { getToken } = useAuth();
  const channelRef = useRef<ReturnType<ReturnType<typeof createAuthedClient>['channel']> | null>(null);
  const onCompletedRef = useRef(onCompleted);
  const onFailedRef = useRef(onFailed);
  const generationIdRef = useRef(generationId);
  const handledIdsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    onCompletedRef.current = onCompleted;
  }, [onCompleted]);

  useEffect(() => {
    onFailedRef.current = onFailed;
  }, [onFailed]);

  useEffect(() => {
    generationIdRef.current = generationId;
  }, [generationId]);

  useEffect(() => {
    if (!projectId || !nodeId) return;
    if (!enabled) {
      // 如果关闭，则主动退订并返回
      if (channelRef.current) {
        try {
          channelRef.current.unsubscribe();
        } catch (_) {}
        channelRef.current = null;
      }
      return;
    }
    const topic = `generation:${projectId}:${nodeId}`;

    (async () => {
      const token = await getToken({ template: 'supabase' });
      const supabase = createAuthedClient(token ?? '');

      // 如果已存在相同主题的频道，避免重复订阅
      if (channelRef.current && (channelRef.current as any).topic === topic) {
        return;
      }

      // 如果已存在不同主题的频道，先取消订阅
      if (channelRef.current) {
        try {
          await (channelRef.current as any).unsubscribe();
        } catch (_) {
          // 忽略卸载时的非关键错误
        }
        channelRef.current = null;
      }

      // 仅订阅 generation 表变更；不使用 broadcast（与项目级实时区分）
      const channel = supabase.channel(topic)
        .on(
          'postgres_changes',
          { event: '*', schema: 'public', table: 'generation', filter: `project_id=eq.${projectId}` },
          (payload) => {
            const row = (payload.new ?? payload.old) as any;
            if (!row || row.node_id !== nodeId) return;
            const rowId = (row as any)?.id as string | undefined;

            if (!generationIdRef.current) return;
            if (rowId && rowId !== generationIdRef.current) return;

            if (row.status === 'completed' && row.output_url) {
              if (rowId && handledIdsRef.current.has(rowId)) return;
              if (rowId) handledIdsRef.current.add(rowId);
              onCompletedRef.current?.(row.output_url as string, row.metadata);
              // The server registered the generated output as a library asset
              // during upload — let the Assets panel pick it up live.
              notifyAssetLibraryChanged();
            } else if (row.status === 'failed') {
              if (rowId && handledIdsRef.current.has(rowId)) return;
              if (rowId) handledIdsRef.current.add(rowId);
              onFailedRef.current?.(row.error as string | undefined);
            }
          }
        );

      // 只对新建的 channel 调用一次 subscribe
      channel.subscribe();
      channelRef.current = channel;
    })();

    return () => {
      if (channelRef.current) {
        channelRef.current.unsubscribe();
        channelRef.current = null;
      }
    };
  }, [projectId, nodeId, getToken, enabled]);
}
